import React, { useMemo } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import { CalendarToday, People, Assignment, TrendingUp } from '@mui/icons-material';
import { useQuery } from '@apollo/client';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import { GET_DASHBOARD_STATS, GET_SHIFTS } from '../graphql/queries';
import { useAuth } from '../context/AuthContext';

const getDateRange = () => {
  const now = new Date();
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 7);
  return { start: start.toISOString(), end: end.toISOString() };
};

const formatTime = (isoDate: string) =>
  new Date(isoDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });

const formatDate = (isoDate: string) =>
  new Date(isoDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const organizationId = user?.organization?.id;
  const range = useMemo(() => getDateRange(), []);

  const { data: statsData, loading: statsLoading, error: statsError } = useQuery(GET_DASHBOARD_STATS, {
    variables: { organizationId },
    skip: !organizationId,
    fetchPolicy: 'cache-and-network',
  });

  const { data: shiftsData, loading: shiftsLoading, error: shiftsError } = useQuery(GET_SHIFTS, {
    variables: {
      organizationId,
      startDate: range.start,
      endDate: range.end,
    },
    skip: !organizationId,
    fetchPolicy: 'cache-and-network',
  });

  const stats = statsData?.dashboardStats || {
    totalStaff: 0,
    shiftsThisWeek: 0,
    pendingRequests: 0,
    attendanceRate: 0,
    coverageRate: 0,
    overtimeHours: 0,
  };

  const cards = [
    { title: 'Total Staff', value: stats.totalStaff, icon: People, color: '#E91E63', bg: 'rgba(233, 30, 99, 0.1)' },
    { title: 'Shifts This Week', value: stats.shiftsThisWeek, icon: CalendarToday, color: '#9C27B0', bg: 'rgba(156, 39, 176, 0.1)' },
    { title: 'Pending Requests', value: stats.pendingRequests, icon: Assignment, color: '#FF9800', bg: 'rgba(255, 152, 0, 0.1)' },
    { title: 'Attendance Rate', value: `${stats.attendanceRate}%`, icon: TrendingUp, color: '#4CAF50', bg: 'rgba(76, 175, 80, 0.1)' },
  ];

  const upcomingShifts = (shiftsData?.shifts ?? []).slice(0, 8);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f0f2f5' }}>
      <Sidebar />
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Header />
        <Box component="main" sx={{ flex: 1, p: 4 }}>
          <Container maxWidth="xl">
            <Box sx={{ mb: 4 }}>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#1a1a2e', mb: 1 }}>
                Welcome, {user?.name || 'Manager'}
              </Typography>
              <Typography variant="body1" sx={{ color: '#666' }}>
                Live workforce overview for your organization.
              </Typography>
            </Box>

            {!organizationId && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Organization context is missing on your account.
              </Alert>
            )}

            {(statsError || shiftsError) && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Some dashboard data could not be loaded.
              </Alert>
            )}

            <Grid container spacing={3} sx={{ mb: 4 }}>
              {cards.map((card) => (
                <Grid item xs={12} sm={6} md={3} key={card.title}>
                  <Card sx={{ borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                            {card.title}
                          </Typography>
                          <Typography variant="h4" sx={{ fontWeight: 800 }}>
                            {card.value}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 3,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: card.bg,
                            color: card.color,
                          }}
                        >
                          <card.icon fontSize="large" />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Card sx={{ borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a2e', mb: 2 }}>
                  Upcoming Shifts (Next 7 Days)
                </Typography>

                {(statsLoading || shiftsLoading) && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={28} />
                  </Box>
                )}

                {!statsLoading && !shiftsLoading && upcomingShifts.length === 0 && (
                  <Alert severity="info">No upcoming shifts were found.</Alert>
                )}

                {!statsLoading &&
                  !shiftsLoading &&
                  upcomingShifts.map((shift: any) => (
                    <Box
                      key={shift.id}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 2,
                        borderRadius: 2,
                        bgcolor: '#fafafa',
                        mb: 1.5,
                      }}
                    >
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {shift.employee?.name || 'Unassigned'} - {shift.department}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666' }}>
                          {formatDate(shift.startDate)} • {formatTime(shift.startDate)} - {formatTime(shift.endDate)}
                        </Typography>
                      </Box>
                      <Chip label={shift.status} size="small" />
                    </Box>
                  ))}
              </CardContent>
            </Card>
          </Container>
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardPage;
